// src/components/layout/HeaderBar.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Shield,
  Bell,
  Search,
  LogOut,
  ChevronDown,
  Menu,
  Calendar,
  Palette,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Users,
  FileText,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../theme/ThemeContext';
import Logo from '../../assets/images/visit_logo.png';

const API_BASE_URL =
   // if you use Vite
  process.env.REACT_APP_API_BASE_URL || // CRA
  'https://patrol-visit.patroll-management.nissiindia.com';

const HeaderBar = ({
  onToggleMobileSidebar,
  onToggleSidebarCollapse,
  isSidebarCollapsed,
  onOpenThemeSelector,
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  // 👤 Logged-in user (from backend)
  const [user, setUser] = useState({
    id: null,
    name: '',
    email: '',
  });

  // 🔍 Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchContainerRef = useRef(null);

  const notifications = [
    { id: 1, message: 'Visit package delivered to ABC Properties', time: '5 min ago' },
    { id: 2, message: 'Guard John Smith started route', time: '1 hour ago' },
    { id: 3, message: '4 visits pending upload', time: '2 hours ago' },
  ];

  /* ------------------------------------------------------
   * Load user profile
   * ------------------------------------------------------ */

  // 1) Load any cached user from storage (fast fallback)
  useEffect(() => {
    const stored =
      localStorage.getItem('authUser') || sessionStorage.getItem('authUser');

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser((prev) => ({
          ...prev,
          ...parsed,
        }));
      } catch (err) {
        console.error('Error parsing authUser from storage', err);
      }
    }
  }, []);

  // 2) Always try to fetch fresh profile from backend
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token =
          localStorage.getItem('authToken') ||
          sessionStorage.getItem('authToken');

        const res = await fetch(`${API_BASE_URL}/api/me`, {
          method: 'GET',
          credentials: 'include', // send cookies if any
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!res.ok) {
          console.warn('[HeaderBar] /api/me failed with status', res.status);
          return;
        }

        const data = await res.json();
console.log('data:', data);
        const safeUser = {
          id: data.id ?? null,
          name: data.name || data.username || 'Admin User',
          email: data.email || '',
        };

        setUser(safeUser);

        // cache minimal info so the header doesn't flash empty on reload
        localStorage.setItem('authUser', JSON.stringify(safeUser));
      } catch (err) {
        console.error('[HeaderBar] Failed to fetch profile:', err);
      }
    };

    fetchProfile();
  }, []);

  /* ------------------------------------------------------
   * Logout
   * ------------------------------------------------------ */

  const handleLogout = () => {
    try {
      // Clear all possible auth tokens
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      localStorage.removeItem('clientAuthToken');
      sessionStorage.removeItem('clientAuthToken');

      // Clear stored user info
      localStorage.removeItem('authUser');
      sessionStorage.removeItem('authUser');

      // Optional: clear auth cookies (if backend uses cookies)
      document.cookie =
        'authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie =
        'clientAuthToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

      setShowProfile(false);
      navigate('/');
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/');
    }
  };

  /* ------------------------------------------------------
   * Search – click outside
   * ------------------------------------------------------ */

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target)
      ) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ------------------------------------------------------
   * Search – backend call
   * ------------------------------------------------------ */

  const runSearch = async (term) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    try {
      setIsSearching(true);
      setSearchError('');
      const res = await fetch(
        `${API_BASE_URL}/api/search?q=${encodeURIComponent(term)}`,
        {
          method: 'GET',
          credentials: 'include', // keep cookies / auth
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Search failed');
      }

      const data = await res.json();
      setSearchResults(data.results || []);
      setShowResults(true);
    } catch (err) {
      console.error('Search error:', err);
      setSearchError(err.message || 'Search failed');
      setSearchResults([]);
      setShowResults(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSearchError('');

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value || value.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      runSearch(value.trim());
    }, 300);
  };

  /* ------------------------------------------------------
   * Search helpers
   * ------------------------------------------------------ */

  const getRecordIcon = (recordType) => {
    switch (recordType) {
      case 'guard':
        return <Users className="w-4 h-4 text-slate-500" />;
      case 'client':
        return <FileText className="w-4 h-4 text-slate-500" />;
      case 'property':
        return <MapPin className="w-4 h-4 text-slate-500" />;
      case 'visit':
        return <Shield className="w-4 h-4 text-slate-500" />;
      default:
        return <Search className="w-4 h-4 text-slate-500" />;
    }
  };

  const getTypeLabelText = (item) => {
    const { recordType, typeLabel, status, extra } = item;

    if (recordType === 'property') {
      return typeLabel ? `Property • ${typeLabel}` : 'Property';
    }

    if (recordType === 'guard') {
      return `Guard • ${status === 'active' ? 'Active' : 'Inactive'}`;
    }

    if (recordType === 'client') {
      return 'Client';
    }

    if (recordType === 'visit') {
      return extra ? `Visit • ${extra}` : 'Visit';
    }

    return '';
  };

  const handleResultClick = (item) => {
    setShowResults(false);
    setSearchQuery('');

    switch (item.recordType) {
      case 'guard':
        navigate(`/guards?highlight=${item.id}`);
        break;
      case 'client':
        navigate(`/clients?highlight=${item.id}`);
        break;
      case 'property':
        navigate(`/properties/${item.id}`);
        break;
      case 'visit':
        navigate(`/visits/${item.id}`);
        break;
      default:
        break;
    }
  };

  // Helper: initials for avatar
  const getInitials = (name) => {
    if (!name) return 'AD';
    return name
      .split(' ')
      .filter(Boolean)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  /* ------------------------------------------------------
   * Render
   * ------------------------------------------------------ */

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* LEFT SECTION */}
          <div className="flex items-center gap-3">
            {/* Mobile Menu Icon (small screens only) */}
            <button
              onClick={onToggleMobileSidebar}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <Menu className="w-5 h-5 text-slate-700" />
            </button>

            {/* Collapse / Expand Icon (desktop only) */}
            <button
              onClick={onToggleSidebarCollapse}
              className="hidden lg:flex items-center justify-center w-9 h-9 rounded-lg hover:bg-slate-100 transition-colors"
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isSidebarCollapsed ? (
                <ChevronRight className="w-5 h-5 text-slate-600" />
              ) : (
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              )}
            </button>

            {/* Logo + Title */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg flex items-center justify-center bg-white">
                <img
                  src={Logo}
                  alt="Logo"
                  className="w-full h-full object-contain p-1"
                />
              </div>

              <div>
                <h1
                  className="text-xl font-bold"
                  style={{
                    background: `linear-gradient(to right, ${theme.primary[600]}, ${theme.secondary[600]})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Patrol Visit by EBlackDog.com
                </h1>
                <p className="text-xs text-slate-500">Admin Panel</p>
              </div>
            </div>
          </div>

          {/* CENTER SEARCH BAR */}
          <div
            className="hidden md:flex flex-1 max-w-xl mx-8"
            ref={searchContainerRef}
          >
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search guards, clients, visits, properties..."
                className="w-full pl-11 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all"
                style={{
                  '--tw-ring-color': `${theme.primary[500]}33`,
                  borderColor: 'rgb(226 232 240)',
                }}
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (searchResults.length > 0) setShowResults(true);
                }}
              />

              {/* Results dropdown */}
              {showResults && (
                <div className="absolute left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-80 overflow-y-auto z-50">
                  {isSearching && (
                    <div className="px-4 py-3 text-sm text-slate-500">
                      Searching...
                    </div>
                  )}

                  {searchError && !isSearching && (
                    <div className="px-4 py-3 text-sm text-red-500">
                      {searchError}
                    </div>
                  )}

                  {!isSearching &&
                    !searchError &&
                    searchResults.length === 0 &&
                    searchQuery.length >= 2 && (
                      <div className="px-4 py-3 text-sm text-slate-500">
                        No results
                      </div>
                    )}

                  {!isSearching &&
                    !searchError &&
                    searchResults.map((item) => (
                      <button
                        key={`${item.recordType}-${item.id}`}
                        className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-start gap-3 border-b last:border-b-0 border-slate-100"
                        onClick={() => handleResultClick(item)}
                      >
                        <div className="mt-0.5">
                          {getRecordIcon(item.recordType)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-800">
                            {item.title}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {getTypeLabelText(item)}
                            {item.code && (
                              <span className="ml-2 text-slate-400">
                                • {item.code}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT SECTION */}
          <div className="flex items-center gap-3">
            {/* Schedule Runs Button */}
            <button
              onClick={() => navigate('/schedule-runs')}
              className="hidden sm:flex items-center gap-2 px-4 py-2 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-200 font-medium"
              style={{
                background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
              }}
            >
              <Calendar className="w-4 h-4" />
              <span className="text-sm">Schedule Run</span>
            </button>

            {/* Theme Selector Button */}
            <button
              onClick={onOpenThemeSelector}
              className="p-2 rounded-xl hover:bg-slate-100 transition-colors relative group"
              title="Change Theme"
            >
              <Palette className="w-5 h-5 text-slate-600" />
              <span className="absolute -bottom-8 right-0 px-2 py-1 rounded text-white bg-slate-800 text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Brand Theme
              </span>
            </button>

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowProfile((v) => !v)}
                className="flex items-center gap-2 p-2 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${theme.primary[500]} 0%, ${theme.secondary[500]} 100%)`,
                  }}
                >
                  <span className="text-white text-sm font-semibold">
                    {getInitials(user.name)}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-600" />
              </button>

              {showProfile && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-200 py-2">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="font-semibold text-slate-800">
                      {user.name || 'Admin User'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {user.email || 'admin@patrolvisit.com'}
                    </p>
                  </div>

                  {/* ✅ Logout */}
                  <button
                    className="w-full px-4 py-2 flex items-center gap-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default HeaderBar;
